/**
 * Session Close Modal
 * Modal for closing a cash register session with closing balance and Z-report display
 */

import { Button, Card, CardContent, CardHeader, CardTitle, Input } from '@/components/ui';
import { useCloseSession } from '@/hooks/pos';
import type { CashRegisterSession, ZReport } from '@/types/pos.types';
import { useTranslation } from '@kgc/i18n';
import { useState } from 'react';

interface SessionCloseModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  session?: CashRegisterSession;
  onSessionClosed: (zReport: ZReport) => void;
}

export function SessionCloseModal({
  open,
  onOpenChange,
  session,
  onSessionClosed,
}: SessionCloseModalProps) {
  const { t } = useTranslation();
  const [closingBalance, setClosingBalance] = useState<string>('');
  const [varianceNote, setVarianceNote] = useState<string>('');
  const [showVarianceInput, setShowVarianceInput] = useState(false);
  const closeSession = useCloseSession();

  const formatPrice = (value: number) => {
    return new Intl.NumberFormat('hu-HU', {
      style: 'currency',
      currency: 'HUF',
      maximumFractionDigits: 0,
    }).format(value);
  };

  // Use expectedBalance from session data if available (set by backend during close).
  // For OPEN sessions this is null — fall back to openingBalance as the baseline.
  // TODO(CRIT-04/M-006): Backend closeSession currently passes cashSalesTotal=0,
  // so expectedBalance = openingBalance even after close. Once the controller
  // injects IPaymentRepository and sums CASH payments, both this value and
  // the Z-report will reflect the real expected cash drawer balance.
  const expectedBalance = session?.expectedBalance ?? session?.openingBalance ?? 0;

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    const balance = parseFloat(closingBalance) || 0;
    const variance = balance - expectedBalance;

    // If there's variance and no note, show note input
    if (variance !== 0 && !varianceNote && !showVarianceInput) {
      setShowVarianceInput(true);
      return;
    }

    if (!session) return;

    try {
      const zReport = await closeSession.mutateAsync({
        sessionId: session.id,
        locationId: session.locationId,
        dto: {
          closingBalance: balance,
          varianceNote: varianceNote || undefined,
        },
      });
      onSessionClosed(zReport);
      resetForm();
      onOpenChange(false);
    } catch (error) {
      console.error('Failed to close session:', error);
    }
  };

  const resetForm = () => {
    setClosingBalance('');
    setVarianceNote('');
    setShowVarianceInput(false);
  };

  const currentBalance = parseFloat(closingBalance) || 0;
  const variance = currentBalance - expectedBalance;

  if (!open || !session) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50">
      <Card className="w-full max-w-md">
        <CardHeader>
          <CardTitle>{t('sales.session.close.title')}</CardTitle>
        </CardHeader>
        <CardContent>
          <form onSubmit={handleSubmit} className="space-y-4">
            {/* Session info */}
            <div className="rounded-lg bg-gray-50 dark:bg-[#1f5f6b] p-4">
              <div className="space-y-2 text-sm">
                <div className="flex justify-between">
                  <span className="text-gray-500 dark:text-[#ccfbf1]">
                    {t('sales.session.close.sessionLabel')}
                  </span>
                  <span className="font-medium">{session.sessionNumber}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-500 dark:text-[#ccfbf1]">
                    {t('sales.session.close.openingBalance')}
                  </span>
                  <span className="font-medium">{formatPrice(session.openingBalance)}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-500 dark:text-[#ccfbf1]">
                    {t('sales.session.close.expectedBalance')}
                  </span>
                  <span className="font-medium">{formatPrice(expectedBalance)}</span>
                </div>
              </div>
            </div>

            {/* Closing balance input */}
            <div>
              <label htmlFor="pos-closing-balance" className="mb-2 block text-sm font-medium">
                {t('sales.session.close.closingBalance')}
              </label>
              <Input
                id="pos-closing-balance"
                type="number"
                value={closingBalance}
                onChange={e => setClosingBalance(e.target.value)}
                placeholder="0"
                min="0"
                step="1"
                autoFocus
                className="text-lg"
              />
            </div>

            {/* Variance display */}
            {closingBalance && (
              <div
                className={`rounded-lg p-3 ${
                  variance === 0
                    ? 'bg-green-50 dark:bg-green-900/20 text-green-700 dark:text-green-400'
                    : 'bg-yellow-50 dark:bg-yellow-900/20 text-yellow-700 dark:text-yellow-400'
                }`}
              >
                <div className="flex justify-between">
                  <span>{t('sales.session.close.variance')}</span>
                  <span className="font-bold">
                    {variance > 0 ? '+' : ''}
                    {formatPrice(variance)}
                  </span>
                </div>
                {variance !== 0 && (
                  <p className="mt-1 text-xs">
                    {variance > 0
                      ? t('sales.session.close.surplus')
                      : t('sales.session.close.shortage')}
                  </p>
                )}
              </div>
            )}

            {/* Variance note input */}
            {showVarianceInput && variance !== 0 && (
              <div>
                <label
                  htmlFor="pos-variance-note"
                  className="mb-2 block text-sm font-medium text-yellow-700"
                >
                  {t('sales.session.close.varianceReason')}
                </label>
                <Input
                  id="pos-variance-note"
                  type="text"
                  value={varianceNote}
                  onChange={e => setVarianceNote(e.target.value)}
                  placeholder={t('sales.session.close.variancePlaceholder')}
                  className="border-yellow-300"
                />
              </div>
            )}

            {/* Error display */}
            {closeSession.isError && (
              <div className="rounded-lg bg-red-50 dark:bg-red-900/20 p-3 text-sm text-red-600 dark:text-red-400">
                {closeSession.error instanceof Error
                  ? closeSession.error.message
                  : t('sales.session.close.closeError')}
              </div>
            )}

            {/* Actions */}
            <div className="flex gap-2 pt-4">
              <Button
                type="button"
                variant="outline"
                onClick={() => {
                  resetForm();
                  onOpenChange(false);
                }}
                className="flex-1"
                disabled={closeSession.isPending}
              >
                {t('sales.session.close.cancel')}
              </Button>
              <Button
                type="submit"
                className="flex-1 bg-red-600 hover:bg-red-700"
                disabled={
                  closeSession.isPending ||
                  !closingBalance ||
                  (showVarianceInput && variance !== 0 && !varianceNote)
                }
              >
                {closeSession.isPending
                  ? t('sales.session.close.closing')
                  : t('sales.session.close.closeButton')}
              </Button>
            </div>
          </form>
        </CardContent>
      </Card>
    </div>
  );
}
